import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  TrendingUp, 
  FileText, 
  Users, 
  AlertCircle,
  Plus,
  ArrowUpRight,
  MapPin,
  Home,
  Warehouse,
  Download,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleDownloadPortfolio = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(`${API}/reports/portfolio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BlueElement_Portfolio_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio report downloaded");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Welcome back, {user?.first_name || "User"}
            </h1>
            <p className="text-slate-400 mt-1">
              Here's an overview of your asset portfolio
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={handleDownloadPortfolio}
              disabled={downloadingReport}
              data-testid="download-portfolio-report-btn"
            >
              {downloadingReport ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Portfolio Report
            </Button>
            <Link to="/assets/new">
              <Button className="btn-primary" data-testid="add-asset-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Assets</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_assets || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-slate-400">
                  <MapPin className="w-4 h-4" /> {stats?.asset_breakdown?.land || 0} Land
                </span>
                <span className="flex items-center gap-1 text-slate-400">
                  <Home className="w-4 h-4" /> {stats?.asset_breakdown?.residential || 0} Residential
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Portfolio Value</p>
                  <p className="text-3xl font-bold text-white mt-1 mono">
                    {formatCurrency(stats?.portfolio_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Leases</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.active_leases || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-cyan-500" />
                </div>
              </div>
              {stats?.expiring_soon > 0 && (
                <div className="mt-4">
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                    {stats.expiring_soon} expiring soon
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Documents</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.documents?.complete || 0}
                    <span className="text-slate-500 text-lg">/{(stats?.documents?.complete || 0) + (stats?.documents?.pending || 0)}</span>
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-violet-500" />
                </div>
              </div>
              {stats?.documents?.pending > 0 && (
                <div className="mt-4">
                  <Badge className="bg-red-500/10 text-red-400 border-red-500/30">
                    {stats.documents.pending} pending
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Notifications */}
          <div className="lg:col-span-2">
            <Card className="card-dashboard">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Notifications & Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-slate-600" />
                    </div>
                    <p className="text-slate-400">No notifications at the moment</p>
                    <p className="text-slate-500 text-sm mt-1">You're all caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.slice(0, 5).map((notification, index) => (
                      <div 
                        key={notification.notif_id || notification.lease_id || `notif-${index}`}
                        className={`p-4 rounded-xl border ${
                          notification.severity === 'error' 
                            ? 'bg-red-500/5 border-red-500/20' 
                            : notification.severity === 'warning'
                            ? 'bg-amber-500/5 border-amber-500/20'
                            : 'bg-blue-500/5 border-blue-500/20'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            notification.severity === 'error' 
                              ? 'bg-red-500' 
                              : notification.severity === 'warning'
                              ? 'bg-amber-500'
                              : 'bg-blue-500'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-white text-sm">{notification.message}</p>
                            <p className="text-slate-500 text-xs mt-1 capitalize">
                              {notification.type.replace(/_/g, ' ')}
                            </p>
                          </div>
                          {notification.asset_id && (
                            <Link to={`/assets/${notification.asset_id}`}>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                View
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <Card className="card-dashboard">
              <CardHeader className="pb-4">
                <CardTitle className="text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/assets/new" className="block">
                  <Button variant="outline" className="w-full justify-start btn-secondary" data-testid="quick-add-asset">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Asset
                  </Button>
                </Link>
                <Link to="/assets" className="block">
                  <Button variant="outline" className="w-full justify-start btn-secondary" data-testid="quick-view-assets">
                    <Building2 className="w-4 h-4 mr-2" />
                    View All Assets
                  </Button>
                </Link>
                <Link to="/leases" className="block">
                  <Button variant="outline" className="w-full justify-start btn-secondary" data-testid="quick-manage-leases">
                    <Users className="w-4 h-4 mr-2" />
                    Manage Leases
                  </Button>
                </Link>
                <Link to="/services" className="block">
                  <Button variant="outline" className="w-full justify-start btn-secondary" data-testid="quick-services">
                    <Warehouse className="w-4 h-4 mr-2" />
                    Request Service
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Subscription Status */}
            <Card className="card-dashboard mt-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-white">Subscription</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <Badge className={`${
                    stats?.subscription === 'enterprise' 
                      ? 'bg-violet-500/10 text-violet-400 border-violet-500/30'
                      : stats?.subscription === 'professional'
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-slate-700/50 text-slate-400 border-slate-600'
                  } capitalize`}>
                    {stats?.subscription || 'Free'} Plan
                  </Badge>
                </div>
                
                {stats?.subscription === 'free' && (
                  <>
                    <p className="text-slate-400 text-sm mb-4">
                      Upgrade to unlock AI appraisals, more assets, and marketplace access.
                    </p>
                    <Link to="/pricing">
                      <Button className="w-full btn-primary" size="sm" data-testid="upgrade-btn">
                        Upgrade Now
                      </Button>
                    </Link>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
